// The creature page's Stone training card (shared/train.ts): +1 base stat per
// step for stones of the creature's own elements plus coins, counted on hunts
// and bosses only. Laid out like the Refine card beside it.
import { ITEM, itemCount } from '../shared/items.ts';
import { pvpTag } from './pvp-tag.ts';
import { SPECIES } from '../shared/species.ts';
import { STAT_NAMES } from '../shared/rolls.ts';
import { creatureStats, type Stats } from '../shared/stats.ts';
import { huntPower } from '../shared/power.ts';
import { MAX_TRAIN_STEPS, TRAIN_STATS, TRAIN_STONES, trainCoinsBetween, trainOf, trainSpent, trainStepCoins, trainStepStones, trainStones, trainStonesBetween } from '../shared/train.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { isArmed } from './confirm.ts';
import { iconUrl } from './assets.ts';
import { esc, full, num } from './fmt.ts';
import { trainPill } from './rollpills.ts';

/** The stat picked in a creature's training card, and the stone picked to pay with (kept while the tab is open). */
const statPick = new Map<number, number>();
const stonePick = new Map<number, string>();
export function pickTrainStat(id: number, stat: number) { statPick.set(id, stat); }
export function pickTrainStone(id: number, item: string) { stonePick.set(id, item); }

/** The stat the card shows: the last one picked, else the one it attacks with. */
function statFor(c: CreatureDTO): number {
  return statPick.get(c.id) ?? (SPECIES[c.species].style === 'might' ? 1 : 3);
}
/** The stone it pays with: the last one picked, else the first of its own with enough for the next step, else the one held most. */
function stoneFor(c: CreatureDTO, next: number): string {
  const ok = trainStones(c.species), picked = stonePick.get(c.id), bag = S.trainer!.bag;
  if (picked && ok.includes(picked)) return picked;
  return ok.find((x) => (bag[x] ?? 0) >= next) ?? [...ok].sort((a, b) => (bag[b] ?? 0) - (bag[a] ?? 0))[0];
}

const statOf = (st: Stats) => [st.hp, st.might, st.guard, st.focus, st.will, st.swift];
const check = (ok: boolean, text: string) => `<li class="${ok ? 'ok' : 'no'}">${ok ? '✓' : '✗'} ${text}</li>`;

/**
 * One quiet line until the trainer holds enough of one of its stones for a first step (or it is trained already);
 * then the stat picker, the preview on hunts, what it costs and the buttons (a second click confirms).
 */
export function trainCard(c: CreatureDTO): string {
  const t = S.trainer!, id = c.id;
  const tr = trainOf(c), stones = trainStones(c.species);
  const trained = tr.some((x) => x > 0);
  if (!trained && !stones.some((x) => (t.bag[x] ?? 0) >= TRAIN_STONES)) {
    // On its own line: as a "· needs …" note beside the title it was too long and dropped below, led by the dot.
    return `<section class="panel cp-card cp-train"><div class="cp-h"><h5>Stone training</h5>${pvpTag('none')}</div><p class="muted">Needs ${esc(stones.map((x) => itemCount(x, TRAIN_STONES)).join(' or '))}.</p></section>`;
  }
  const stat = statFor(c), now = tr[stat], next = now + 1;
  const stone = stoneFor(c, trainStepStones(next)), have = t.bag[stone] ?? 0;
  const needStones = trainStepStones(next), needCoins = trainStepCoins(next);
  const before = statOf(creatureStats(c))[stat];
  const plus = tr.map((n, i) => (i === stat ? n + 1 : n));
  const after = statOf(creatureStats({ ...c, train: plus }))[stat];
  // The most steps affordable in one go (up to MAX_TRAIN_STEPS), offered once more than one is.
  let most = 0;
  while (most < MAX_TRAIN_STEPS && trainStonesBetween(now, now + most + 1) <= have && trainCoinsBetween(now, now + most + 1) <= t.coins) most++;
  const why = c.listed ? 'Listed for sale' : have < needStones ? `Needs ${itemCount(stone, needStones)}` : t.coins < needCoins ? `Needs ${full(needCoins)} coins` : '';
  const btn = (n: number, primary: boolean) => {
    const qty = trainStonesBetween(now, now + n), coins = trainCoinsBetween(now, now + n);
    const armed = isArmed(`train:${id}:${stat}:${n}`);
    const cost = `${itemCount(stone, qty)}, ${num(coins)} coins`;
    return `<button class="btn${primary ? ' gold' : ''}${armed ? ' armed' : ''}" data-act="train" data-id="${id}" data-stat="${stat}" data-qty="${n}" data-item="${stone}"${why ? ' disabled' : ''}>${armed ? `Confirm: use ${esc(itemCount(stone, qty))} and ${full(coins)} coins` : `Train to +${now + n} · ${esc(cost)}`}</button>`;
  };
  const payWith = stones.length > 1 ? `<div class="seg" role="group" aria-label="Pay with">${stones.map((it) => `<button class="btn small${it === stone ? ' on' : ''}" data-act="train-item" data-id="${id}" data-item="${it}" aria-pressed="${it === stone}"><img class="ico16" src="${iconUrl(it)}" alt="">${esc(ITEM[it].name)}<small>${full(t.bag[it] ?? 0)}</small></button>`).join('')}</div>` : '';
  const spent = trainSpent(c);
  return `<section class="panel cp-card cp-train"><div class="cp-h"><img class="cp-ico" src="${iconUrl(stone)}" alt=""><h5>Stone training</h5>${pvpTag('none')}${trainPill(c)}</div>
    <div class="seg" role="group" aria-label="Stat to train">${TRAIN_STATS.map((i) => `<button class="btn small${i === stat ? ' on' : ''}" data-act="train-pick" data-id="${id}" data-stat="${i}" aria-pressed="${i === stat}">${STAT_NAMES[i]}${tr[i] ? `<small>+${tr[i]}</small>` : ''}</button>`).join('')}</div>
    <p class="cp-prev">On hunts and bosses: ${STAT_NAMES[stat]} ${full(before)} → <b>${full(after)}</b> · Power ${full(huntPower(c))} → <b>${full(huntPower({ ...c, train: plus }))}</b></p>
    ${payWith}
    <ul class="cp-check">${check(have >= needStones, `${esc(itemCount(stone, needStones))} for +${next} (you have ${full(have)})`)}${check(t.coins >= needCoins, `${full(needCoins)} coins (you have ${num(t.coins)})`)}</ul>
    <p class="muted">${[1, 2, 3].map((k) => `+${now + k}: ${full(trainStepStones(now + k))} · ${num(trainStepCoins(now + k))}`).join(' / ')}${spent ? ` · used ${full(spent)} stones` : ''}</p>
    <div class="row cost">${btn(1, true)}${most > 1 ? btn(most, false) : ''}</div>
    ${c.listed ? `<p class="cp-why">${esc(why)}</p>` : ''}</section>`;
}
